import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  updateProfile,
  deleteUser as deleteAuthUser
} from 'firebase/auth';
import { doc, setDoc, getDoc, collection, getDocs, deleteDoc, updateDoc, query, where, writeBatch } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { tenantService } from './tenantService';

export const authService = {
  // Register new user
  async register(email, password, userData) {
    try {
      console.log('Registration - userData:', userData);
      console.log('Registration - role:', userData.role);
      console.log('Registration - role type:', typeof userData.role);
      
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Update profile with display name
      await updateProfile(user, {
        displayName: userData.displayName || userData.role
      });
      
      // Create user document in Firestore
      const userDocData = {
        uid: user.uid,
        email: user.email,
        role: userData.role,
        displayName: userData.displayName,
        createdAt: new Date(),
        status: 'active',
        ...userData
      };
      
      console.log('Registration - storing user document:', userDocData);
      
      await setDoc(doc(db, 'users', user.uid), userDocData);
      
      return user;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  },

  // Sign in user
  async login(email, password) {
    try {
      // First, try to sign in normally
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Check if this user needs to create their Firebase Auth account
      const userData = await this.getUserData(user.uid);
      if (userData && userData.requiresAccountCreation && userData.role === 'tenant') {
        // This tenant needs to create their Firebase Auth account
        // We'll use the stored default password to create it
        await tenantService.createFirebaseAuthAccount(user.uid, email, userData.defaultPassword);
        
        // Now sign in with the new account
        await signOut(auth);
        const newUserCredential = await signInWithEmailAndPassword(auth, email, userData.defaultPassword);
        return newUserCredential.user;
      }
      
      return user;
    } catch (error) {
      throw error;
    }
  },

  // Sign out user
  async logout() {
    try {
      await signOut(auth);
    } catch (error) {
      throw error;
    }
  },

  // Get current user
  getCurrentUser() {
    return auth.currentUser;
  },

  // Listen to auth state changes
  onAuthStateChanged(callback) {
    return onAuthStateChanged(auth, callback);
  },

  // Get user data from Firestore
  async getUserData(uid) {
    try {
      console.log('getUserData - fetching for UID:', uid);
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        console.log('getUserData - retrieved data:', userData);
        console.log('getUserData - role:', userData.role);
        console.log('getUserData - role type:', typeof userData.role);
        return userData;
      }
      console.log('getUserData - no user document found');
      return null;
    } catch (error) {
      console.error('getUserData error:', error);
      throw error;
    }
  },

  // Create user document in Firestore (for recovery when doc is missing)
  async createUserDocument(uid, data) {
    try {
      const userDocData = {
        uid,
        email: data.email,
        role: data.role || 'owner',
        displayName: data.displayName || data.email?.split('@')[0] || 'User',
        status: 'active',
        createdAt: new Date(),
        updatedAt: new Date(),
        ...data
      };
      await setDoc(doc(db, 'users', uid), userDocData);
      return userDocData;
    } catch (error) {
      console.error('createUserDocument error:', error);
      throw error;
    }
  },

  // Update user data in Firestore
  async updateUserData(uid, userData) {
    try {
      await setDoc(doc(db, 'users', uid), {
        ...userData,
        updatedAt: new Date()
      }, { merge: true });
    } catch (error) {
      throw error;
    }
  },

  // Admin: Get all users
  async getAllUsers() {
    try {
      const usersCollection = collection(db, 'users');
      const usersSnapshot = await getDocs(usersCollection);
      const users = [];
      
      usersSnapshot.forEach((doc) => {
        users.push({
          uid: doc.id,
          ...doc.data()
        });
      });
      
      return users;
    } catch (error) {
      console.error('Error getting all users:', error);
      throw error;
    }
  },

  // Admin: Suspend user
  async suspendUser(uid) {
    try {
      await updateDoc(doc(db, 'users', uid), {
        status: 'suspended',
        suspendedAt: new Date(),
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error suspending user:', error);
      throw error;
    }
  },

  // Admin: Activate user
  async activateUser(uid) {
    try {
      await updateDoc(doc(db, 'users', uid), {
        status: 'active',
        activatedAt: new Date(),
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error activating user:', error);
      throw error;
    }
  },

  // Admin: Delete user
  async deleteUser(uid) {
    try {
      console.log(`Starting comprehensive deletion for user: ${uid}`);
      
      // Get user data first to understand what needs to be deleted
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (!userDoc.exists()) {
        throw new Error('User document not found');
      }
      
      const userData = userDoc.data();
      console.log('User data to be deleted:', userData);
      
      // Create a batch for atomic operations
      const batch = writeBatch(db);
      
      // 1. Delete user document
      batch.delete(doc(db, 'users', uid));
      console.log('Added user document deletion to batch');
      
      // 2. Delete user's properties (if they're a property owner)
      if (userData.role === 'owner') {
        const propertiesQuery = query(collection(db, 'properties'), where('ownerId', '==', uid));
        const propertiesSnapshot = await getDocs(propertiesQuery);
        
        propertiesSnapshot.forEach((propertyDoc) => {
          batch.delete(propertyDoc.ref);
          console.log(`Added property deletion to batch: ${propertyDoc.id}`);
        });
        
        // Delete property expenses
        const expensesQuery = query(collection(db, 'expenses'), where('propertyOwnerId', '==', uid));
        const expensesSnapshot = await getDocs(expensesQuery);
        
        expensesSnapshot.forEach((expenseDoc) => {
          batch.delete(expenseDoc.ref);
          console.log(`Added expense deletion to batch: ${expenseDoc.id}`);
        });
      }
      
      // 3. Delete user's tickets (both as owner and service provider)
      const ticketsQuery = query(collection(db, 'tickets'), where('userId', '==', uid));
      const ticketsSnapshot = await getDocs(ticketsQuery);
      
      ticketsSnapshot.forEach((ticketDoc) => {
        batch.delete(ticketDoc.ref);
        console.log(`Added ticket deletion to batch: ${ticketDoc.id}`);
      });
      
      // Also delete tickets where user is the service provider
      const providerTicketsQuery = query(collection(db, 'tickets'), where('serviceProviderId', '==', uid));
      const providerTicketsSnapshot = await getDocs(providerTicketsQuery);
      
      providerTicketsSnapshot.forEach((ticketDoc) => {
        batch.delete(ticketDoc.ref);
        console.log(`Added provider ticket deletion to batch: ${ticketDoc.id}`);
      });
      
      // 4. Delete user's bids (if they're a service provider)
      if (userData.role === 'serviceProvider') {
        const bidsQuery = query(collection(db, 'bids'), where('serviceProviderId', '==', uid));
        const bidsSnapshot = await getDocs(bidsQuery);
        
        bidsSnapshot.forEach((bidDoc) => {
          batch.delete(bidDoc.ref);
          console.log(`Added bid deletion to batch: ${bidDoc.id}`);
        });
      }
      
      // 5. Delete user's payments and payment methods
      const paymentsQuery = query(collection(db, 'payments'), where('userId', '==', uid));
      const paymentsSnapshot = await getDocs(paymentsQuery);
      
      paymentsSnapshot.forEach((paymentDoc) => {
        batch.delete(paymentDoc.ref);
        console.log(`Added payment deletion to batch: ${paymentDoc.id}`);
      });
      
      // 6. Delete user's chat messages
      const chatMessagesQuery = query(collection(db, 'chatMessages'), where('userId', '==', uid));
      const chatMessagesSnapshot = await getDocs(chatMessagesQuery);
      
      chatMessagesSnapshot.forEach((messageDoc) => {
        batch.delete(messageDoc.ref);
        console.log(`Added chat message deletion to batch: ${messageDoc.id}`);
      });
      
      // 7. Delete user's notifications
      const notificationsQuery = query(collection(db, 'notifications'), where('userId', '==', uid));
      const notificationsSnapshot = await getDocs(notificationsQuery);
      
      notificationsSnapshot.forEach((notificationDoc) => {
        batch.delete(notificationDoc.ref);
        console.log(`Added notification deletion to batch: ${notificationDoc.id}`);
      });
      
      // 8. Delete referral records (if they were referred by someone)
      if (userData.referredBy) {
        const referralsQuery = query(collection(db, 'referrals'), where('providerId', '==', uid));
        const referralsSnapshot = await getDocs(referralsQuery);
        
        referralsSnapshot.forEach((referralDoc) => {
          batch.delete(referralDoc.ref);
          console.log(`Added referral deletion to batch: ${referralDoc.id}`);
        });
      }
      
      // 9. Delete user's documents
      const documentsQuery = query(collection(db, 'documents'), where('userId', '==', uid));
      const documentsSnapshot = await getDocs(documentsQuery);
      
      documentsSnapshot.forEach((documentDoc) => {
        batch.delete(documentDoc.ref);
        console.log(`Added document deletion to batch: ${documentDoc.id}`);
      });
      
      // 10. Delete user's tenant records (if they're a tenant)
      if (userData.role === 'tenant') {
        const tenantRecordsQuery = query(collection(db, 'tenantRecords'), where('tenantId', '==', uid));
        const tenantRecordsSnapshot = await getDocs(tenantRecordsQuery);
        
        tenantRecordsSnapshot.forEach((tenantRecordDoc) => {
          batch.delete(tenantRecordDoc.ref);
          console.log(`Added tenant record deletion to batch: ${tenantRecordDoc.id}`);
        });
      }
      
      // Execute all deletions in a single batch
      await batch.commit();
      console.log(`Successfully deleted user ${uid} and all related data`);
      
      return {
        success: true,
        message: `User and all related data deleted successfully`,
        deletedCollections: [
          'users',
          'properties',
          'expenses', 
          'tickets',
          'bids',
          'payments',
          'chatMessages',
          'notifications',
          'referrals',
          'documents',
          'tenantRecords'
        ]
      };
      
    } catch (error) {
      console.error('Error deleting user:', error);
      throw new Error(`Failed to delete user: ${error.message}`);
    }
  },

  // Note: Firebase Authentication deletion requires backend implementation
  // For now, this function deletes all Firestore data
  // To delete the auth account, implement Firebase Admin SDK on backend
  async deleteUserAuthAccount(uid) {
    console.warn('Firebase Auth account deletion requires backend implementation with Firebase Admin SDK');
    console.warn('For now, only Firestore data is deleted. Auth account remains active.');
    return {
      success: false,
      message: 'Firebase Auth account deletion requires backend implementation',
      note: 'Contact your backend developer to implement Firebase Admin SDK for auth account deletion'
    };
  }
}; 